import { ReactNode, useEffect, useState, useCallback } from "react";
import localstorage from '@/pages/Root/services/storage/localstorage';
import { ThemeContext } from "./themeContext";

export type Theme = "light" | "dark";

const THEME_KEY = "forcedAppTheme";

// Helper to get current system preference
function getSystemPreference(): Theme {
  if (import.meta.env.SSR) return "dark"; // Default for SSR
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getInitialTheme(): Theme {
  if (import.meta.env.SSR) return "dark";

  const storedTheme = localstorage?.get(THEME_KEY) as Theme | null;
  if (storedTheme === "light" || storedTheme === "dark") {
    return storedTheme;
  }
  return getSystemPreference();
}

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Core function to apply theme to state and DOM
  // This function does NOT interact with localStorage directly for "forcing"
  const applyTheme = useCallback((newTheme: Theme) => {
    if (import.meta.env.SSR || (newTheme !== "light" && newTheme !== "dark")) {
      return;
    }

    setThemeState(newTheme);
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(newTheme);
    document.body.classList.remove("light", "dark");
    document.body.classList.add(newTheme);
    // Keep CSS color-scheme in sync so light-dark() resolves correctly when supported
    document.documentElement.style.colorScheme = newTheme;
  }, []); // setThemeState is stable

  const forceThemeChange = useCallback((forcedTheme: Theme) => {
    if (forcedTheme !== "light" && forcedTheme !== "dark") return;
    localstorage?.set(THEME_KEY, forcedTheme);
    applyTheme(forcedTheme);
  }, [applyTheme]);

  const clearForcedTheme = useCallback(() => {
    localstorage?.remove(THEME_KEY);
    // After clearing, immediately apply the current system preference
    applyTheme(getSystemPreference());
  }, [applyTheme]);

  // Effect 1: Apply the initial theme and handle changes to the theme state.
  // This ensures that on mount, the theme (from localStorage or system) is applied to the DOM.
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]); // theme is the state, applyTheme is stable

  // Effect 2: Listen to OS/browser preference changes.
  useEffect(() => {
    if (import.meta.env.SSR) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      // Only update if the user hasn't explicitly forced a theme.
      if (!localstorage?.get(THEME_KEY)) {
        applyTheme(mediaQuery.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [applyTheme]); // applyTheme is stable

  // Effect 3: Listen to localStorage changes (e.g., from other tabs) and custom 'theme-change' event.
  useEffect(() => {
    if (import.meta.env.SSR) return;

    const handleExternalChange = () => {
      const storedTheme = localstorage?.get(THEME_KEY) as Theme | null;
      if (storedTheme) {
        // If a theme is forced externally, apply it
        if (theme !== storedTheme) { // Only apply if different from current state
          applyTheme(storedTheme);
        }
      } else {
        // If the forced theme was cleared externally, revert to system preference
        // but only if the current theme is not already matching the system preference
        // or if the current theme was the one that just got cleared.
        const systemPreference = getSystemPreference();
        if (theme !== systemPreference || localstorage?.get(THEME_KEY) === null) {
          // Check localstorage again in case this event is slightly delayed
          if (!localstorage?.get(THEME_KEY)) {
            applyTheme(systemPreference);
          }
        }
      }
    };

    window.addEventListener('storage', handleExternalChange);
    window.addEventListener('theme-change', handleExternalChange); // Assuming 'theme-change' implies a check on localStorage

    return () => {
      window.removeEventListener('storage', handleExternalChange);
      window.removeEventListener('theme-change', handleExternalChange);
    };
  }, [applyTheme, theme]); // Include theme to re-evaluate conditions if current theme changes

  return (
    <ThemeContext.Provider value={{ theme, setTheme: forceThemeChange, forceThemeChange, clearForcedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
